import { useState, useEffect, useCallback } from 'react'
import { useBestaetigung } from '@pilot-hub/ui'
import { ListenTabelle, type SpalteDef } from '../components/ListenTabelle'
import { usePersistState, FilterZurueck, KennzahlenLeiste } from '../components/ListenWerkzeuge'
import { FormAktionsleiste, useUngespeichert } from '../components/Formular'
import { ZeilenAktionen } from '../components/ZeilenAktionen'
import { StatusBadge } from '../components/StatusBadge'
import { PageHeader, Card, Button, Input, Select } from '@pilot-hub/ui'
import { vehicleApi } from '@pilot-hub/api-client'
import { ArrowLeft, Plus, Edit, Eye, Trash2, Save } from 'lucide-react'

interface Fahrzeug {
  id: number
  rufzeichen: string
  fahrzeugtyp: string
  marke: string | null
  modell: string | null
  baujahr: number | null
  max_einsatzkraefte: number
  aktiv: boolean
  standort: string | null
  status: string | null
  besitzer_typ: string | null
  besitzer_nutzer_id: number | null
  versicherung_nummer: string | null
  naechste_hu: string | null
  naechste_wartung: string | null
  bemerkungen: string | null
  erstellt_am: string | null
}

const LEER = {
  rufzeichen: '',
  fahrzeugtyp: '',
  marke: '',
  modell: '',
  baujahr: '',
  max_einsatzkraefte: '4',
  aktiv: 'true'
}

const TYPEN = ['GW-Drohne', 'ELW', 'GW-Hundestaffel', 'KTW', 'RTW', 'GW-San', 'MTW', 'OrgL-RD', 'Baby-NEF', 'Einsatzfahrzeug']

type ViewMode = 'list' | 'create' | 'edit' | 'detail'
type ViewState = { mode: ViewMode; fahrzeugId?: number }

export function VehiclesPage() {
  const { bestaetigen, dialog } = useBestaetigung()
  const [fahrzeuge, setFahrzeuge] = useState<Fahrzeug[]>([])
  const [search, setSearch] = usePersistState('fahrzeuge.search', '')
  const [aktivFilter, setAktivFilter] = usePersistState('fahrzeuge.aktivFilter', 'all')
  const [loading, setLoading] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)

  const [view, setView] = useState<ViewState>({ mode: 'list' })
  const [form, setForm] = useState<Record<string, string>>({ ...LEER })
  const [speichert, setSpeichert] = useState(false)

  const geaendert = useUngespeichert(form, JSON.stringify(view))
  const laden = useCallback(async () => {
    setLoading(true); setFehler(null)
    try {
      const r = await vehicleApi.list({ size: 500 })
      setFahrzeuge((r.data as any).items ?? (r.data as any) ?? [])
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Fahrzeuge konnten nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { laden() }, [laden])

  function oeffneNeu() {
    setForm({ ...LEER })
    setView({ mode: 'create' })
  }

  function oeffneBearbeiten(f: Fahrzeug) {
    setForm({
      rufzeichen: f.rufzeichen ?? '',
      fahrzeugtyp: f.fahrzeugtyp ?? '',
      marke: f.marke ?? '',
      modell: f.modell ?? '',
      baujahr: String(f.baujahr ?? ''),
      max_einsatzkraefte: String(f.max_einsatzkraefte ?? 4),
      aktiv: String(f.aktiv ?? true)
    })
    setView({ mode: 'edit', fahrzeugId: f.id })
  }

  function oeffneDetail(f: Fahrzeug) {
    setView({ mode: 'detail', fahrzeugId: f.id })
    setForm({
      rufzeichen: f.rufzeichen,
      fahrzeugtyp: f.fahrzeugtyp,
      marke: f.marke || '',
      modell: f.modell || '',
      baujahr: String(f.baujahr ?? ''),
      max_einsatzkraefte: String(f.max_einsatzkraefte ?? '4'),
      aktiv: String(f.aktiv),
      standort: f.standort || '',
      status: f.status || '',
      besitzer_typ: f.besitzer_typ || '',
      besitzer_nutzer_id: String(f.besitzer_nutzer_id ?? ''),
      versicherung_nummer: f.versicherung_nummer || '',
      naechste_hu: f.naechste_hu ? f.naechste_hu.slice(0, 10) : '',
      naechste_wartung: f.naechste_wartung ? f.naechste_wartung.slice(0, 10) : '',
      bemerkungen: f.bemerkungen || '',
    })
  }

  function zurueckZurListe() {
    setView({ mode: 'list' })
    setForm({ ...LEER })
  }

  async function speichern() {
    if (!form.rufzeichen || !form.fahrzeugtyp) return
    setSpeichert(true)
    try {
      const payload: any = {
        rufzeichen: form.rufzeichen,
        fahrzeugtyp: form.fahrzeugtyp,
        marke: form.marke || null,
        modell: form.modell || null,
        baujahr: form.baujahr ? Number(form.baujahr) : null,
        max_einsatzkraefte: Number(form.max_einsatzkraefte) || 4,
        aktiv: form.aktiv === 'true'
      }
      if (view.mode === 'edit' && view.fahrzeugId) await vehicleApi.update(view.fahrzeugId, payload)
      else await vehicleApi.create(payload)
      zurueckZurListe()
      await laden()
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Speichern fehlgeschlagen')
    } finally {
      setSpeichert(false)
    }
  }

  async function loeschen(id: number) {
    try {
      await vehicleApi.delete(id)
      await laden()
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Löschen fehlgeschlagen')
    }
  }

  const gefiltert = fahrzeuge.filter(f =>
    (aktivFilter === 'all' || (aktivFilter === 'aktiv') === !!f.aktiv) && (
    f.rufzeichen.toLowerCase().includes(search.toLowerCase()) ||
    f.fahrzeugtyp.toLowerCase().includes(search.toLowerCase()))
  )

  const columns: SpalteDef[] = [
    { key: 'rufzeichen', header: 'Rufzeichen', accessor: 'rufzeichen' },
    { key: 'fahrzeugtyp', header: 'Fahrzeugtyp', accessor: 'fahrzeugtyp' },
    { key: 'marke_modell', header: 'Marke / Modell', accessor: 'id', sortWert: (f: any) => `${f.marke ?? ''} ${f.modell ?? ''}`.trim() || null, render: (_v: any, f: any) => `${f.marke ?? ''} ${f.modell ?? ''}`.trim() || '—' },
    { key: 'baujahr', header: 'Baujahr', accessor: 'baujahr', render: (_v: any, f: any) => f.baujahr ?? '—' },
    { key: 'max_crew', header: 'Max. Besatzung', accessor: 'max_einsatzkraefte', mobile: 'hidden' },
    { key: 'status', header: 'Status', accessor: 'aktiv', sortWert: (f: any) => f.aktiv ? 0 : 1, render: (v: any) => {
      return <StatusBadge wert={v ? 'AKTIV' : 'INAKTIV'} />
    }},
    {
      key: 'actions', header: 'Aktionen', accessor: 'id', render: (_v: any, row: any) => (
        <ZeilenAktionen name={String(row.rufzeichen)} onBearbeiten={() => oeffneBearbeiten(row)} onLoeschen={async () => { const name = row.rufzeichen; if (await bestaetigen({ titel: 'Fahrzeug löschen?', text: 'Fahrzeug', objekt: String(name), hinweis: 'Das kann nicht rückgängig gemacht werden.' })) vehicleApi.delete(row.id).then(laden) }} />
      )
    },
  ]

  // ====== LIST VIEW ======
  if (view.mode === 'list') {
    return (
      <div className="space-y-6">
        <PageHeader
          title=""
          subtitle="Alle Fahrzeuge der ASB Drohnenstaffel verwalten"
          action={<Button onClick={oeffneNeu}><Plus className="h-4 w-4 mr-2" />Neues Fahrzeug</Button>}
        />

        {fehler && <div className="rounded-lg border border-primary-600/40 bg-primary-600/10 px-4 py-3 text-sm text-red-200">{fehler}</div>}

        <Card>
          <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-start">
            <Input placeholder="Nach Rufzeichen oder Typ suchen..." value={search} onChange={e => setSearch(e.target.value)} className="w-full sm:w-72" />
            <FilterZurueck aktiv={!!search || aktivFilter !== 'all'} onClick={() => { setSearch(''); setAktivFilter('all') }} />
            <div className="text-sm text-dark-text-muted whitespace-nowrap sm:ml-auto">{gefiltert.length} von {fahrzeuge.length}</div>
          </div>
          <div className="mb-4"><KennzahlenLeiste items={[
            { label: 'Alle Fahrzeuge', anzahl: fahrzeuge.length, aktiv: aktivFilter === 'all', onClick: () => setAktivFilter('all') },
            { label: 'Aktiv', anzahl: fahrzeuge.filter(f => f.aktiv).length, aktiv: aktivFilter === 'aktiv', punkt: 'bg-emerald-400', onClick: () => setAktivFilter(aktivFilter === 'aktiv' ? 'all' : 'aktiv') },
            { label: 'Inaktiv', anzahl: fahrzeuge.filter(f => !f.aktiv).length, aktiv: aktivFilter === 'inaktiv', punkt: 'bg-slate-400', onClick: () => setAktivFilter(aktivFilter === 'inaktiv' ? 'all' : 'inaktiv') },
          ]} /></div>

          {loading ? <div className="py-8 text-center text-dark-text-muted">Lade Fahrzeuge…</div> : (
            <ListenTabelle persistKey="fahrzeuge" data={gefiltert} columns={columns} keyAccessor={(f: any) => String(f.id)} onRowClick={oeffneDetail} emptyMessage="Keine Fahrzeuge vorhanden" />
          )}
        </Card>
        {dialog}
      </div>
    )
  }

  // ====== DETAIL / CREATE / EDIT VIEW ======
  const currentFahrzeug = view.fahrzeugId ? fahrzeuge.find(f => f.id === view.fahrzeugId) : null
  const isCreate = view.mode === 'create'
  const isEdit = view.mode === 'edit'
  const isDetail = view.mode === 'detail'

  return (
    <div className="space-y-6">
      <PageHeader
        title={isCreate ? 'Neues Fahrzeug' : isEdit ? 'Fahrzeug bearbeiten' : 'Fahrzeug anzeigen'}
        subtitle={isCreate ? 'Neues Fahrzeug anlegen' : currentFahrzeug ? currentFahrzeug.rufzeichen : ''}
        action={
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={zurueckZurListe}><ArrowLeft className="h-4 w-4 mr-1" />Zurück</Button>
            {isDetail && currentFahrzeug && <Button onClick={() => oeffneBearbeiten(currentFahrzeug)}><Edit className="h-4 w-4 mr-1" />Bearbeiten</Button>}
          </div>
        }
      />

      {fehler && <div className="rounded-lg border border-primary-600/40 bg-primary-600/10 px-4 py-3 text-sm text-red-200">{fehler}</div>}

      <Card>
        <div className="space-y-4">
          <div>
            <label className="label">Rufzeichen *</label>
            <Input value={form.rufzeichen} onChange={e => setForm({...form, rufzeichen: e.target.value})} disabled={isDetail} placeholder="z.B. FLORIAN 10/1" />
          </div>
          <div>
            <label className="label">Fahrzeugtyp *</label>
            <Select value={form.fahrzeugtyp} onChange={e => setForm({...form, fahrzeugtyp: e.target.value})} disabled={isDetail}
              options={TYPEN.map(t => ({ value: t, label: t }))} placeholder="— Typ wählen —" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Marke</label>
              <Input value={form.marke} onChange={e => setForm({...form, marke: e.target.value})} disabled={isDetail} placeholder="z.B. Mercedes" />
            </div>
            <div>
              <label className="label">Modell</label>
              <Input value={form.modell} onChange={e => setForm({...form, modell: e.target.value})} disabled={isDetail} placeholder="z.B. Sprinter" />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Baujahr</label>
              <Input type="number" value={form.baujahr} onChange={e => setForm({...form, baujahr: e.target.value})} disabled={isDetail} placeholder="z.B. 2020" min="1950" max={new Date().getFullYear() + 1} />
            </div>
            <div>
              <label className="label">Max. Besatzung</label>
              <Input type="number" value={form.max_einsatzkraefte} onChange={e => setForm({...form, max_einsatzkraefte: e.target.value})} disabled={isDetail} placeholder="4" min="1" max="20" />
            </div>
          </div>
          <div>
            <label className="label">Status</label>
            <Select value={form.aktiv} onChange={e => setForm({...form, aktiv: e.target.value})} disabled={isDetail}
              options={[{ value: 'true', label: 'Aktiv' }, { value: 'false', label: 'Inaktiv' }]} placeholder="— Status wählen —" />
          </div>

          {isDetail && currentFahrzeug && (
            <>
              <div className="pt-4 border-t border-dark-border">
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-dark-text-muted">Marke:</span> <span>{currentFahrzeug.marke || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-dark-text-muted">Modell:</span> <span>{currentFahrzeug.modell || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-dark-text-muted">Baujahr:</span> <span>{currentFahrzeug.baujahr ?? '—'}</span></div>
                  <div className="flex justify-between"><span className="text-dark-text-muted">Max. Besatzung:</span> <span>{currentFahrzeug.max_einsatzkraefte}</span></div>
                  <div className="flex justify-between"><span className="text-dark-text-muted">Status:</span> <StatusBadge wert={currentFahrzeug.aktiv ? 'AKTIV' : 'INAKTIV'} /></div>
                </dl>
              </div>
            </>
          )}
        </div>
      </Card>
      {!isDetail && <FormAktionsleiste geaendert={geaendert} speichert={speichert} speichernGesperrt={!form.rufzeichen || !form.fahrzeugtyp} onSpeichern={speichern} onAbbrechen={zurueckZurListe} />}
    </div>
  )
}